import React, { useEffect, useState } from "react";
import { useParams, useLocation } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Container,
  Grid,
  Link,
  Paper,
  Step,
  StepLabel,
  Stepper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useMediaQuery,
  Switch
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import {
  BsArrowUpRight,
  BsArrowRight,
  BsChevronDown,
  BsChevronUp
} from "react-icons/bs";
import employerSvc from "../../services/employer/employerSvc";

// Steps
const STEPS = [
  <Typography variant="body1" key="step1">
    <strong>Choose a vendor</strong> and open an investment account
  </Typography>,
  <Typography variant="body1" key="step2">
    <strong>Return here</strong> to set your salary deferral
  </Typography>,
  <Typography variant="body1" key="step3">
    <strong>Finished!</strong> Access your account anytime for adjustments.
  </Typography>
];

// Step Icon (blue circle with white text)
const CustomStepIcon = (props) => (
  <Box
    sx={{
      width: 24,
      height: 24,
      borderRadius: "50%",
      backgroundColor: "#041E42",
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      color: "white",
      fontWeight: "bold",
      fontSize: "14px"
    }}
  >
    {props.icon}
  </Box>
);

// Helper to fix double https:// issue
const fixEstablishAccountURL = (url) => {
  if (!url) return "";
  return url.startsWith("http") ? url : `https://${url}`;
};

export default function ApprovedVendors() {
  const { employerId } = useParams();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const planTypeRaw = searchParams.get("planType");
  const planType = planTypeRaw
    ? planTypeRaw.replace("(", "").replace(")", "").replace(" ", "")
    : "";

  const { reliusWeb: reliusWebFromState } = location.state || {};

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(false);
  const [approvedVendors, setApprovedVendors] = useState([]);
  const [employerInfo, setEmployerInfo] = useState({
    name: "",
    state: "",
    reliusWeb: ""
  });

  // For mobile: track which vendor cards are expanded
  const [expandedCards, setExpandedCards] = useState({});

  useEffect(() => {
    const getApprovedVendors = async () => {
      try {
        setLoading(true);
        const { employerInfo: apiEmployerInfo, approvedVendors } =
          await employerSvc.getEmployerDetails({
            employerIdForURL: employerId,
            page: "enrollSearch"
          });

        // Filter by planType if needed
        const filteredVendors = approvedVendors.filter(
          (av) => av.planType.replace(" ", "") === planType
        );

        setApprovedVendors(filteredVendors);
        setEmployerInfo({
          id: employerId,
          name: apiEmployerInfo.employer_name || apiEmployerInfo.employerName,
          state: apiEmployerInfo.state,
          reliusWeb: apiEmployerInfo.relius_web || reliusWebFromState
        });
      } catch (err) {
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    getApprovedVendors();
  }, [employerId, planType, reliusWebFromState]);

  // Initialize expanded state for mobile
  useEffect(() => {
    const initState = {};
    approvedVendors.forEach((_, index) => {
      initState[index] = false;
    });
    setExpandedCards(initState);
  }, [approvedVendors]);

  // Hidden form submission for "Set Contributions"
  const handleClick = (e, data) => {
    e.preventDefault();
    const url = `https://${employerInfo.reliusWeb}/enrollinplan.aspx`;
    const formData = {
      LoginButton: "true",
      RANDOMNUM: "",
      PLANID: "",
      PIN: data.planPassword,
      DROP: ""
    };
    post(url, formData);
  };

  function post(path, params, method = "post", target = "_blank") {
    const form = document.createElement("form");
    form.method = method;
    form.action = path;
    form.target = target; // <-- This ensures the form submission opens in a new tab
  
    for (const key in params) {
      if (Object.hasOwnProperty.call(params, key)) {
        const hiddenField = document.createElement("input");
        hiddenField.type = "hidden";
        hiddenField.name = key;
        hiddenField.value = params[key];
        form.appendChild(hiddenField);
      }
    }
  
    document.body.appendChild(form);
    form.submit();
  }


  // Hyperlink converter for phone or website text
  const hyperlinkThis = (data) => {
    if (!data) return "";
    if (data.startsWith("www") || data.startsWith("http")) {
      return (
        <Link
          href={data.startsWith("www") ? `https://${data}` : data}
          target="_blank"
          rel="noopener"
          underline="hover"
        >
          {data}
        </Link>
      );
    }
    return data;
  };

  // Desktop: Vendor table
  const ThreeColumnTable = () => (
    <TableContainer component={Paper} sx={{ mt: 2 }}>
      <Table>
        <TableHead sx={{ backgroundColor: "#f5f5f5" }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 600 }}>Vendor Options</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Step one: Establish your Account</TableCell>
            <TableCell sx={{ fontWeight: 600 }}>Step two: Set your contributions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {approvedVendors.map((vendor, idx) => (
            <TableRow key={idx}>
              <TableCell>
                <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                  {vendor.vendorName}
                </Typography>
                <Typography variant="body2">{vendor.phoneNumber}</Typography>
                <Typography variant="body2">{hyperlinkThis(vendor.webSite)}</Typography>
              </TableCell>
              <TableCell>
                {vendor.establishAccount ? (
                  <Link
                    href={fixEstablishAccountURL(vendor.establishAccount)}
                    target="_blank"
                    rel="noopener"
                    underline="hover"
                    sx={{
                      cursor: "pointer",
                      fontWeight: 500,
                      display: "inline-flex",
                      alignItems: "center",
                      gap: 0.5
                    }}
                  >
                    Open Account <BsArrowUpRight />
                  </Link>
                ) : (
                  "N/A"
                )}
              </TableCell>
              <TableCell>
                <Link
                  component="button"
                  variant="body2"
                  underline="hover"
                  onClick={(e) => handleClick(e, vendor)}
                  sx={{
                    cursor: "pointer",
                    fontWeight: 500,
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 0.5
                  }}
                >
                  Set Contributions <BsArrowRight />
                </Link>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );

  // Mobile: Collapsible vendor cards
  const MobileVendorCards = () => {
    const toggleCard = (index) => {
      setExpandedCards((prev) => ({
        ...prev,
        [index]: !prev[index]
      }));
    };

    const handleSwitch = (e) => {
      const checked = e.target.checked;
      const newState = {};
      approvedVendors.forEach((_, index) => {
        newState[index] = checked;
      });
      setExpandedCards(newState);
    };

    const allExpanded = Object.values(expandedCards).every((v) => v);

    return (
      <>
        <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
          <Switch checked={allExpanded} onChange={handleSwitch} />
          <Typography variant="body2" ml={1}>
            Expand
          </Typography>
        </Box>

        <Grid container spacing={2}>
          {approvedVendors.map((vendor, index) => {
            const isExpanded = expandedCards[index];
            return (
              <Grid item xs={12} key={index}>
                <Card
                  sx={{
                    border: "1px solid #c3c3c3",
                    borderRadius: 1,
                    boxShadow: 0,
                    transition: "all 0.3s"
                  }}
                >
                  <CardContent
                    sx={{
                      // Override MUI default last-child padding
                      p: 1.5, // 12px on all sides
                      "&:last-child": {
                        paddingBottom: 1.5 // also 12px
                      }
                    }}
                  >
                    {/* Header row (always visible) */}
                    <Grid
                      container
                      alignItems="center"
                      justifyContent="space-between"
                      onClick={() => toggleCard(index)}
                      sx={{ cursor: "pointer" }}
                    >
                      <Grid item>
                        <Typography variant="subtitle1" sx={{ fontWeight: "bold" }}>
                          {vendor.vendorName}
                        </Typography>
                      </Grid>
                      <Grid item>
                        {isExpanded ? <BsChevronUp size={18} /> : <BsChevronDown size={18} />}
                      </Grid>
                    </Grid>

                    {/* Expanded content */}
                    {isExpanded && (
                      <Box sx={{ mt: 2 }}>
                        <Typography variant="body2" sx={{ mb: 1 }}>
                          {vendor.phoneNumber}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          {hyperlinkThis(vendor.webSite)}
                        </Typography>

                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mb: 1 }}>
                          Step One: Establish your Account
                        </Typography>
                        <Typography variant="body2" gutterBottom>
                          {vendor.establishAccount ? (
                            <Link
                              href={fixEstablishAccountURL(vendor.establishAccount)}
                              target="_blank"
                              rel="noopener"
                              underline="hover"
                              sx={{
                                cursor: "pointer",
                                fontWeight: 500,
                                display: "inline-flex",
                                alignItems: "center",
                                gap: 0.5
                              }}
                            >
                              Open Account <BsArrowUpRight size={14} />
                            </Link>
                          ) : (
                            "N/A"
                          )}
                        </Typography>

                        <Typography variant="subtitle2" sx={{ fontWeight: "bold", mt: 2, mb: 1 }}>
                          Step Two: Set your Contributions
                        </Typography>
                        <Link
                          component="button"
                          variant="body2"
                          underline="hover"
                          onClick={(e) => handleClick(e, vendor)}
                          sx={{
                            cursor: "pointer",
                            fontWeight: 500,
                            display: "inline-flex",
                            alignItems: "center",
                            gap: 0.5
                          }}
                        >
                          Set Contributions <BsArrowRight size={14} />
                        </Link>
                      </Box>
                    )}
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </>
    );
  };

  return (
    <Container maxWidth="md" sx={{ mt: isMobile ? 2 : 4 }}>
      {loading ? (
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          height="100%"
        >
          <CircularProgress size={48} />
        </Box>
      ) : (
        <Grid container direction="column" spacing={3}>
          {/* Steps */}
          <Grid item>
            <Stepper
              activeStep={-1}
              orientation={isMobile ? "vertical" : "horizontal"}
              sx={{ mb: 4 }}
              alternativeLabel={!isMobile}
            >
              {STEPS.map((label, index) => (
                <Step key={index} active>
                  <StepLabel StepIconComponent={CustomStepIcon}>
                    {label}
                  </StepLabel>
                </Step>
              ))}
            </Stepper>
          </Grid>

          {/* Vendor List Title */}
          <Grid item>
            <Typography
              variant="h5"
              align="center"
              sx={{ mb: 2, fontWeight: "bold" }}
            >
              {employerInfo.name} Approved Vendors
            </Typography>
          </Grid>

          {/* Conditional: Desktop table vs. Mobile cards */}
          <Grid item>
            {isMobile ? <MobileVendorCards /> : <ThreeColumnTable />}
          </Grid>
        </Grid>
      )}
    </Container>
  );
}
